import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Download, Loader2, FileText, CheckCircle } from 'lucide-react';
import { useEPSESite } from '@/hooks/useEPSESites';
import { useEPSEQuarterData } from '@/hooks/useEPSEQuarterData';
import { useEPSEProfiles } from '@/hooks/useEPSEProfiles';
import { useEPSESimulations } from '@/hooks/useEPSESimulations';
import { generateEPSEReport, getReportFilename } from '@/lib/epsePDFGenerator';
import { validateQuarterData } from '@/lib/epseDataValidation';
import { toast } from 'sonner';

export default function CRMEPSEReport() {
  const { siteId } = useParams<{ siteId: string }>();
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  
  const { data: site, isLoading: siteLoading } = useEPSESite(siteId);
  const { quarterData } = useEPSEQuarterData(siteId);
  const { profiles } = useEPSEProfiles(siteId);
  const { simulations } = useEPSESimulations(siteId);
  
  const latestSimulation = simulations?.find(s => s.status === 'completed');
  
  const handleGenerateReport = async () => {
    if (!site || !profiles) return;
    
    setIsGenerating(true);
    try {
      const qualityReport = quarterData 
        ? validateQuarterData(quarterData as any, site.annual_kwh_total ?? undefined)
        : undefined;
      
      const dateRange = quarterData && quarterData.length > 0 ? {
        start: new Date(quarterData[0].start_time).toLocaleDateString('nl-NL'),
        end: new Date(quarterData[quarterData.length - 1].start_time).toLocaleDateString('nl-NL'),
      } : undefined;
      
      const blob = await generateEPSEReport({
        site,
        profiles,
        simulation: latestSimulation,
        qualityReport,
        quarterDataCount: quarterData?.length || 0,
        dateRange,
      });
      
      // Download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = getReportFilename(site.name);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      toast.success('Rapport gedownload');
    } catch (error) {
      console.error('Report generation failed:', error);
      toast.error('Fout bij genereren rapport');
    } finally {
      setIsGenerating(false);
    }
  };
  
  if (siteLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }
  
  if (!site) {
    return (
      <div className="p-6 text-center">
        <p>Site niet gevonden</p>
        <Button onClick={() => navigate('/crm/analyses/epse')} className="mt-4">
          Terug naar overzicht
        </Button>
      </div>
    );
  }
  
  const hasData = quarterData && quarterData.length > 0;
  const hasProfiles = profiles && profiles.length > 0;
  const hasSimulation = !!latestSimulation;
  
  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/crm/analyses/epse/${siteId}`)}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">PDF Rapport</h1>
          <p className="text-muted-foreground">{site.name}</p>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Rapportinhoud
            </CardTitle>
            <CardDescription>
              Het rapport bevat de volgende secties
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <ReportSection 
                title="1. Inleiding & Data Overview" 
                available={true}
                description="Sitegegevens, contractlimiet, dataperiode"
              />
              <ReportSection 
                title="2. Data Processing" 
                available={hasData}
                description="Tijdcontextlabeling, datakwaliteit"
              />
              <ReportSection 
                title="3. Methodologie" 
                available={true}
                description="Uitleg berekeningen en aannames"
              />
              <ReportSection 
                title="4. Profielanalyse" 
                available={hasProfiles}
                description="Statistieken, seizoensvariatie"
              />
              <ReportSection 
                title="5. BESS Simulatie" 
                available={hasSimulation}
                description="Configuratie en resultaten"
              />
              <ReportSection 
                title="6. Interpretatie" 
                available={hasData}
                description="Conclusies en aanbevelingen"
              />
              <ReportSection 
                title="7. Bijlagen" 
                available={true}
                description="Formules, aannames, disclaimer"
              />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle>Genereer Rapport</CardTitle>
            <CardDescription>
              Download een volledig PDF rapport met alle analyse en simulatieresultaten
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-muted-foreground">Kwartierwaarden</p>
                <p className="font-medium">{quarterData?.length?.toLocaleString('nl-NL') || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Profielen</p>
                <p className="font-medium">{profiles?.length || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Simulaties</p>
                <p className="font-medium">{simulations?.filter(s => s.status === 'completed').length || 0}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Contractlimiet</p>
                <p className="font-medium">{site.connection_kw_limit} kW</p>
              </div>
            </div>
            
            {!hasData && (
              <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm">
                <p className="font-medium text-amber-600">Geen data beschikbaar</p>
                <p className="text-muted-foreground">
                  Upload eerst kwartierdata om een volledig rapport te genereren.
                </p>
              </div>
            )}
            
            <Button 
              onClick={handleGenerateReport} 
              disabled={isGenerating || !hasProfiles}
              className="w-full"
              size="lg"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Genereren...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF Rapport
                </>
              )}
            </Button>
            
            <p className="text-xs text-muted-foreground text-center">
              Bestandsnaam: {getReportFilename(site.name)}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function ReportSection({ 
  title, 
  available, 
  description 
}: { 
  title: string; 
  available: boolean; 
  description: string;
}) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${available ? 'bg-green-500/5' : 'bg-muted/50'}`}>
      <CheckCircle className={`h-5 w-5 mt-0.5 ${available ? 'text-green-600' : 'text-muted-foreground'}`} />
      <div>
        <p className={`font-medium ${!available && 'text-muted-foreground'}`}>{title}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
